/**
 * Twin entities resource — typed wrappers over `/v1/twin/entities` plus
 * the server↔UI mappers (B5.1).
 *
 * The server's `data` column is opaque UI-owned JSON. Our seed
 * (scripts/seed-twin-plant.sql) stores the full PlantNode blob
 * ({ instanceName, position, entity }) so a seeded plant hydrates the
 * process graph identically to the fixture path. Rows authored by the
 * copilot / manual editor may carry `{}` — the mapper falls back to the
 * key columns + a minimal synthetic EntityDef so the graph never breaks.
 */

import { api } from "@/lib/api-client";
import type {
  EntityClass,
  EntityDef,
  PlantNode,
} from "@foundry/foundry-plant";
import { qs } from "./qs";
import type { TwinEntity, TwinEntityList } from "./types";

export interface TwinEntityCreate {
  id: string;
  kind: string;
  name: string;
  template_id: string;
  data: unknown;
}

export const twinEntities = {
  list: (params?: { org_id?: string; limit?: number; offset?: number }) =>
    api.get<TwinEntityList>(`/v1/twin/entities${qs(params)}`),
  get: (id: string) => api.get<TwinEntity>(`/v1/twin/entities/${id}`),
  create: (body: TwinEntityCreate) =>
    api.post<TwinEntity>("/v1/twin/entities", body),
  remove: (id: string) => api.delete<void>(`/v1/twin/entities/${id}`),
};

interface PlantNodeBlob {
  instanceName?: string;
  position?: { x: number; y: number };
  entity?: EntityDef;
}

function asBlob(data: unknown): PlantNodeBlob {
  return data && typeof data === "object" ? (data as PlantNodeBlob) : {};
}

/**
 * Build a minimal EntityDef when the server row has no embedded `entity`
 * (e.g. a copilot-created entity that only set kind/name/template_id).
 * Keeps the graph renderable — empty ports/params, neutral icon.
 */
function syntheticEntity(e: TwinEntity): EntityDef {
  const klass = (["STATION", "EQUIPMENT", "INSTRUMENT", "TRANSPORT"] as const).includes(
    e.kind as EntityClass,
  )
    ? (e.kind as EntityClass)
    : "EQUIPMENT";
  return {
    id: e.id,
    templateId: e.template_id,
    class: klass,
    lotOp: "PASSTHROUGH",
    iconKey: "cog",
    oneLine: "",
    inputPorts: [],
    outputPorts: [],
    parameters: [],
  };
}

/**
 * Ensure the required array fields exist. Some stored `data.entity` blobs omit
 * them (e.g. import-created equipment on the batch-lane orgs), and shared render
 * code iterates them unguarded (`lineForEntity` spreads the port arrays,
 * `ParamActivationStrip` filters parameters) — a single missing array
 * white-screens the whole twin surface. Normalizing here fixes every consumer at
 * the data boundary, for every org.
 */
function normalizeEntity(entity: EntityDef): EntityDef {
  return {
    ...entity,
    inputPorts: entity.inputPorts ?? [],
    outputPorts: entity.outputPorts ?? [],
    parameters: entity.parameters ?? [],
  };
}

/** Server TwinEntity → UI PlantNode. */
export function toPlantNode(e: TwinEntity): PlantNode {
  const blob = asBlob(e.data);
  return {
    id: e.id,
    instanceName: blob.instanceName ?? e.name,
    position: blob.position ?? { x: 0, y: 0 },
    entity: blob.entity ? normalizeEntity(blob.entity) : syntheticEntity(e),
  };
}

/** UI PlantNode → server create/upsert body (round-trips the full blob). */
export function plantNodeToCreate(node: PlantNode): TwinEntityCreate {
  return {
    id: node.id,
    kind: node.entity.class,
    name: node.instanceName,
    template_id: node.entity.templateId,
    data: {
      instanceName: node.instanceName,
      position: node.position,
      entity: node.entity,
    },
  };
}
